import React, { useEffect, useRef } from "react";
import { fs, ink, T, withAlpha } from "./tokens";

// Small Life Areas atoms (Astria UI Master: Button, Badge, Tag, ProgressBar,
// Toggle) plus the shared dark modal shell.

type BtnVariant = "primary" | "secondary" | "ghost" | "danger" | "area";
export const Button: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: BtnVariant;
    areaColor?: string;
    block?: boolean;
    size?: "sm" | "md" | "lg";
  }
> = ({ variant = "primary", areaColor, block, size = "md", style, className, ...rest }) => {
  const pad = size === "lg" ? "13px 20px" : size === "sm" ? "6px 12px" : "9px 18px";
  const tint = areaColor || T.color.primary;
  const variants: Record<BtnVariant, React.CSSProperties> = {
    primary: { background: T.color.primaryButton, color: "#fff", boxShadow: T.shadow.glowPrimary },
    secondary: {
      background: T.color.bg.overlay,
      color: T.color.text.primary,
      borderColor: withAlpha(T.color.secondary, 0.35),
    },
    ghost: { background: "transparent", color: T.color.text.secondary },
    danger: {
      background: withAlpha(T.color.semantic.error, 0.15),
      color: ink(T.color.semantic.error),
      borderColor: withAlpha(T.color.semantic.error, 0.4),
    },
    area: {
      background: withAlpha(tint, 0.16),
      color: T.color.text.primary,
      borderColor: withAlpha(tint, 0.55),
    },
  };
  return (
    <button
      type="button"
      className={`lg2-btn ${className || ""}`}
      style={{
        ...fs(size === "lg" ? "base" : "sm", 600),
        border: "1px solid transparent",
        borderRadius: T.radius.lg,
        padding: pad,
        width: block ? "100%" : undefined,
        cursor: rest.disabled ? "not-allowed" : "pointer",
        opacity: rest.disabled ? 0.5 : 1,
        ...variants[variant],
        ...style,
      }}
      {...rest}
    />
  );
};

export const Badge: React.FC<{
  color: string;
  children: React.ReactNode;
  title?: string;
  size?: "sm" | "md";
}> = ({ color, children, title, size = "sm" }) => (

  <span
    title={title}
    style={{
      ...fs(size === "md" ? "sm" : "xs", 600),
      color: ink(color),
      background: withAlpha(color, 0.14),
      border: `1px solid ${withAlpha(color, 0.5)}`,
      borderRadius: T.radius.full,
      padding: size === "md" ? "3px 12px" : "1px 8px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

export const Tag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      ...fs("xs"),
      color: T.color.text.secondary,
      background: T.color.bg.overlay,
      borderRadius: T.radius.md,
      padding: "2px 8px",
    }}
  >
    {children}
  </span>
);

export const ProgressBar: React.FC<{
  value: number; // 0..100
  color: string;
  height?: number;
  label?: string;
}> = ({ value, color, height = 6, label }) => {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(v)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      style={{
        height,
        width: "100%",
        borderRadius: T.radius.full,
        background: withAlpha(color, 0.18),
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${v}%`,
          height: "100%",
          borderRadius: T.radius.full,
          background: color,
          transition: `width ${T.ms.slow}ms ${T.ease.spring}`,
        }}
      />
    </div>
  );
};

export const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}> = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    style={{
      position: "relative",
      width: 40,
      height: 22,
      flexShrink: 0,
      borderRadius: T.radius.full,
      border: "none",
      cursor: "pointer",
      background: checked ? T.color.primaryButton : T.color.toggleOff,
      transition: `background ${T.ms.fast}ms ${T.ease.default}`,
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 2,
        left: checked ? 20 : 2,
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: "#fff",
        boxShadow: T.shadow.sm,
        transition: `left ${T.ms.fast}ms ${T.ease.default}`,
      }}
    />
  </button>
);

const FOCUSABLE =
  'button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])';

// Dark bottom-sheet / dialog: focus moves in, is trapped, Escape closes, and
// focus returns to the trigger. `focusKey` re-focuses when a flow changes step.
export const ModalShell: React.FC<{
  ariaLabel: string;
  title: React.ReactNode;
  onClose: () => void;
  focusKey?: unknown;
  children: React.ReactNode;
}> = ({ ariaLabel, title, onClose, focusKey, children }) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(document.activeElement);

  useEffect(() => {
    sheetRef.current
      ?.querySelector<HTMLElement>(
        "button:not([aria-label='Close']):not([disabled]), input, textarea, select",
      )
      ?.focus();
  }, [focusKey]);
  useEffect(() => {
    const trigger = triggerRef.current as HTMLElement | null;
    return () => trigger?.focus?.();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const nodes = sheetRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (!nodes?.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      style={{ background: T.color.backdrop }}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        className="lg2-modal w-full max-w-lg rounded-t-3xl sm:rounded-3xl"
        style={{
          background: T.color.bg.modal,
          border: `1px solid ${T.color.bg.overlay}`,
          boxShadow: T.shadow.lg,
          color: T.color.text.primary,
          fontFamily: T.font.family,
          padding: 20,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 style={{ ...fs("md", 600), margin: 0 }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              ...fs("lg"),
              color: T.color.text.secondary,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};
